import { useEffect, useRef, useSyncExternalStore } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { activeDistrictStore } from './activeDistrictStore'

/**
 * Side-effect-only hook — renders nothing.
 *
 * Clears the React Query cache when the active management area (district)
 * changes to a *different* one within the same authenticated session. Every
 * district-scoped read (`/api/v1/teams`, `/api/v1/users`, season observations,
 * documents, …) is cached under a district-agnostic query key but fetched with
 * the current `X-District-Id` header; without this, switching areas serves the
 * previous area's cached data until each query happens to refetch (the "toggle
 * areas a couple of times and it corrects itself" symptom).
 *
 * Only a genuine district → different-district switch clears. The initial
 * resolution (null → first district on startup) is skipped so a legitimately
 * warming cache is not thrown away, and the sign-out / account-switch cases
 * (district → null, or a new uid) are left to
 * `useClearQueryCacheOnUserChange`, which owns identity changes.
 *
 * Mount this once inside <QueryClientProvider> alongside
 * `useClearQueryCacheOnUserChange`.
 */
export function useClearQueryCacheOnDistrictChange(): void {
  const queryClient = useQueryClient()
  const activeDistrictId = useSyncExternalStore(
    activeDistrictStore.subscribe,
    activeDistrictStore.snapshot,
    activeDistrictStore.snapshot,
  )

  // undefined = not yet observed (record only); string | null thereafter.
  const prevRef = useRef<string | null | undefined>(undefined)

  useEffect(() => {
    const prev = prevRef.current
    prevRef.current = activeDistrictId

    if (prev === undefined) return // first observation — record without clearing

    // Only an in-session switch between two real districts clears the cache.
    // null endpoints (startup warm-up, sign-out) are handled elsewhere.
    if (prev !== null && activeDistrictId !== null && activeDistrictId !== prev) {
      queryClient.clear()
    }
  }, [activeDistrictId, queryClient])
}
